import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  ApartmentOutlined,
  BadgeOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  EditOutlined,
  EventRepeatOutlined,
  ScheduleOutlined,
  StorageOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { ViewActionFooter } from "../Shared/ViewActionFooter";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { LoaderWithoutHeight } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import { PayInputActivityLog } from "../Shared/PayInputActivityLog";
import { DateField, SelectField, TextField, TimeField, toOptions } from "../Shared/fieldInputs";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { formatDate, formatExactQty, formatMoney } from "../Shared/formatters";
import { designColors, designShell } from "services/constants/designTokens";
import { DAY_TYPES, BASIS_OPTIONS, validateForm, previewShiftEarning, formatPayableQty } from "./shiftEarningsData";
import { useShiftSchedules } from "./useShiftSchedules";
import { loadShiftDayTypes, loadShiftFormOptions, loadUserPerDayPerHourSalary } from "./shiftEarningsApi";

// Label with the red required asterisk.
const required = (label) => (
  <>
    {label} <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
  </>
);

// Add + Edit. `initial` is a form object (see emptyForm / rowToForm).
// `source` is the entry's source when editing: Manual and Import entries
// are fully editable; Attendance entries come from attendance, so only
// their Payroll Cycle can be changed here.
export const ShiftEarningFormPanel = ({
  open,
  mode,
  initial,
  source,
  onClose,
  onSave,
  expanded,
  onToggleExpand,
  saving,
}) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [options, setOptions] = useState({ employees: [], shifts: [], errors: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setOptionsLoading(true);
    setOptions({ employees: [], shifts: [], errors: [] });
    loadShiftFormOptions(domain, topbarCompanyId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setOptions(data); })
      .catch((error) => { if (!controller.signal.aborted) setOptions({ employees: [], shifts: [], errors: [error.message] }); })
      .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    return () => controller.abort();
  }, [open, domain, topbarCompanyId, retry]);
  // Day Type options come from the server; the built-in list is used until
  // they load or if they can't be loaded.
  const [dayTypes, setDayTypes] = useState(DAY_TYPES);
  const [dayTypesLoading, setDayTypesLoading] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setDayTypesLoading(true);
    loadShiftDayTypes(domain, controller.signal)
      .then((list) => { if (!controller.signal.aborted && list.length) setDayTypes(list); })
      .catch(() => { if (!controller.signal.aborted) setDayTypes(DAY_TYPES); })
      .finally(() => { if (!controller.signal.aborted) setDayTypesLoading(false); });
    return () => controller.abort();
  }, [open, domain]);
  const locked = mode === "edit" && source === "Attendance";
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setShowErrors(false);
    }
  }, [open, initial]);

  const errors = useMemo(() => (form ? validateForm(form, mode === "edit" ? initial : null) : {}), [form, mode, initial]);
  const totals = useMemo(() => (form ? previewShiftEarning(form, mode === "edit" ? initial : null) : null), [form, mode, initial]);

  const { schedules, error: scheduleError, loading: schedulesLoading, retry: retrySchedules } = useShiftSchedules(domain, [form?.employee_code], open);

  // Rate auto-fill: looks up this employee's per-day/per-hour pay rate and
  // fills Rate from whichever one matches Basis — on Add, and also when
  // re-opening Edit on a Manual entry (same as Working Hours below).
  // Attendance-sourced (locked) entries are left alone.
  const employeeUserId = options.employees.find((option) => option.value === form?.employee_code)?.user_id || "";
  const [salaryRates, setSalaryRates] = useState(null);
  useEffect(() => {
    setSalaryRates(null);
    if (!open || locked || !employeeUserId) return;
    const controller = new AbortController();
    loadUserPerDayPerHourSalary(domain, {
      userId: employeeUserId,
      payScheduleId: form?.pay_schedule_id,
      date: form?.date,
      signal: controller.signal,
    })
      .then((data) => { if (!controller.signal.aborted && data) setSalaryRates(data); })
      .catch(() => {});
    return () => controller.abort();
  }, [open, locked, domain, employeeUserId, form?.pay_schedule_id, form?.date]);

  useEffect(() => {
    if (!salaryRates) return;
    const rate = form?.basis === "hours" ? salaryRates.per_hour : salaryRates.per_day;
    if (rate != null) setForm((current) => (current ? { ...current, rate: String(rate) } : current));
  }, [salaryRates, form?.basis]);

  // Opening Edit on an already manually-added entry re-syncs Working Hours
  // to the shift's current configured duration — picks up changes made to
  // the shift's setup since this entry was saved. Attendance-sourced
  // entries stay untouched (locked): their hours are actual clock-in/out,
  // not the shift's nominal duration.
  useEffect(() => {
    if (!open || mode !== "edit" || locked) return;
    const duration = options.shifts.find((option) => option.value === form?.shift_name)?.duration;
    if (duration) setForm((current) => (current ? { ...current, worked_shift_hours: duration } : current));
  }, [open, mode, locked, form?.shift_name, options.shifts]);

  if (!form) return null;

  const employeeOptions = [...options.employees];
  if (form.employee_code && !employeeOptions.some((option) => option.value === form.employee_code)) employeeOptions.push({ value: form.employee_code, id: form.employee_code, employee_id: form.employee_code, label: initial.employee_name || form.employee_code, image: initial.image_url || "", image_url: initial.image_url || "" });
  const selectedEmployee = employeeOptions.find((option) => option.value === form.employee_code) ?? null;
  const shiftOptions = [...options.shifts];
  if (form.shift_name && !shiftOptions.some((option) => option.value === form.shift_name)) shiftOptions.push({ value: form.shift_name, label: form.shift_name });
  const cycleOptions = [...schedules];
  if (form.pay_schedule_id && !cycleOptions.some((option) => option.value === form.pay_schedule_id)) cycleOptions.push({ value: form.pay_schedule_id, label: initial.payroll_cycle || form.pay_schedule_id });
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value, ...(key === "employee_code" ? { pay_schedule_id: "" } : {}) }));
  const visibleError = (key) => (showErrors ? errors[key] : undefined);

  const handleSave = () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }
    onSave(form);
  };

  const numberInput = (key) => (
    <TextField
      type="number"
      value={form[key]}
      disabled={locked || saving}
      error={visibleError(key)}
      onChange={set(key)}
    />
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={mode === "edit" ? "Edit Shift Earnings" : "Add Shift Earnings"}
      subtitle={
        locked
          ? "This entry comes from ERP (Attendance). Only the payroll cycle can be changed."
          : mode === "edit"
            ? "Update this entry. The payable amount is recalculated."
            : "Manually add a shift earnings entry for an employee."
      }
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={saving ? undefined : onClose}
          primaryLabel={mode === "edit" ? "Save Changes" : "Add"}
          primaryProps={{ onClick: handleSave, loading: saving, disabled: saving || optionsLoading || schedulesLoading }}
        />
      }
    >
      {/* Employees and shifts load first; the form shows once they are in. */}
      {optionsLoading ? <LoaderWithoutHeight info="Loading..." /> : (
      <Mui.Stack spacing={2}>
        {options.errors.length ? <Mui.Alert severity="warning" action={<Mui.Button onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{options.errors.join(" ")}</Mui.Alert> : null}
        {scheduleError ? <Mui.Alert severity="warning" action={<Mui.Button onClick={retrySchedules}>Retry</Mui.Button>}>{scheduleError}</Mui.Alert> : null}
        {locked ? (
          <RequestInfoBanner
            title="Shift earning details (read only)"
            body="This record is created from ERP (Attendance). Only the payroll cycle can be changed."
          />
        ) : null}
        <FormSection title="Employee">
          <Mui.Box sx={{
            gridColumn: "1 / -1",
            ...(mode === "edit" ? {
              "& .MuiInputBase-input.Mui-disabled": {
                color: designColors.slate900,
                WebkitTextFillColor: designColors.slate900,
                opacity: 1,
              },
            } : {}),
          }}>
            <Field
              label={required("Employee")}
              helper="Employee who worked the shift. Search by name or employee code."
              error={visibleError("employee_code")}
            >
              <CustomAutoCompleteWithLogo
                dataList={employeeOptions}
                selectedValue={selectedEmployee}
                updateDataFunction={(event, option) => set("employee_code")(option?.value ?? "")}
                placeHolder="Select employee"
                textBoxError={Boolean(visibleError("employee_code"))}
                disableAction={mode === "edit" || saving || optionsLoading}
                showAvathar={!selectedEmployee}
              />
            </Field>
          </Mui.Box>
        </FormSection>
        {/* Picked employee's card on its own (not inside the white section
            card, which washed out its teal background), same as Team
            Workspace Approvals' Apply panels. */}
        {selectedEmployee ? (
          <SelectedEmployeeHeader
            selectedUser={{
              ...selectedEmployee,
              department_name: selectedEmployee.department_name || initial.department_name,
              designation: selectedEmployee.designation || initial.designation,
            }}
          />
        ) : null}

        <FormSection title="Payroll Details">
          {/* Helpers on paired fields stay one line (≤ ~30 characters): a
              longer one wraps and pushes its input below its neighbour's. */}
          <Field label={required("Payroll Cycle")} helper="Payroll run that pays this.">
            <SelectField
              value={form.pay_schedule_id}
              onChange={set("pay_schedule_id")}
              options={[{ value: "", label: mode === "edit" ? "Unassigned" : "Automatic" }, ...cycleOptions]}
              disabled={saving || schedulesLoading || !form.employee_code}
              placeholder={schedulesLoading ? "Loading payroll cycles..." : "Select payroll cycle"}
            />
          </Field>
          <Field label="Source" helper="Where this entry came from.">
            <TextField
              value={mode === "edit" ? (locked ? "Attendance (ERP)" : source || "–") : "Manual"}
              disabled
              onChange={() => {}}
            />
          </Field>
        </FormSection>

        <FormSection title="Shift Earning Details">
          {/* The date can't be changed once saved (the server refuses it). */}
          <Field label={required("Date")} helper={mode === "edit" ? "Can't be changed once saved." : "Date the shift was worked."} error={visibleError("date")}>
            <DateField
              value={form.date}
              disabled={locked || saving || mode === "edit"}
              error={visibleError("date")}
              onChange={set("date")}
            />
          </Field>
          <Field label={required("Day Type")} helper="Type of day worked." error={visibleError("earning_type")}>
            <SelectField
              value={form.earning_type}
              disabled={locked || saving}
              onChange={set("earning_type")}
              error={visibleError("earning_type")}
              options={toOptions([...new Set([...dayTypes, form.earning_type].filter(Boolean))])}
              placeholder={dayTypesLoading ? "Loading day types..." : "Select day type"}
            />
          </Field>
          <Field label={required("Shift")} helper="Shift the employee worked." error={visibleError("shift_name")}>
            <SelectField
              value={form.shift_name}
              disabled={locked || saving}
              onChange={(value) => {
                // Picking a shift fills Working Hours from its configured
                // duration — the user can still edit it afterward if the
                // actual hours worked differ.
                const duration = shiftOptions.find((option) => option.value === value)?.duration;
                setForm((current) => ({
                  ...current,
                  shift_name: value,
                  ...(duration ? { worked_shift_hours: duration } : {}),
                }));
              }}
              error={visibleError("shift_name")}
              options={shiftOptions}
              placeholder="Select shift"
            />
          </Field>
          {/* 24-hour clock picker, same as Overtime's hours field. */}
          <Field label="Working Hours" helper="Hours worked (24-hour, HH:MM)." error={visibleError("worked_shift_hours")}>
            <TimeField
              value={form.worked_shift_hours}
              disabled={locked || saving}
              error={visibleError("worked_shift_hours")}
              onChange={set("worked_shift_hours")}
            />
          </Field>
          <Field label={required("Basis")} helper="Pay per shift or per hour." error={visibleError("basis")}>
            <SelectField
              value={form.basis}
              disabled={locked || saving}
              onChange={set("basis")}
              error={visibleError("basis")}
              options={BASIS_OPTIONS}
              placeholder="Select basis"
            />
          </Field>
          <Field label={required("Credited Quantity")} helper="Shifts or hours to be paid." error={visibleError("quantity")}>
            {numberInput("quantity")}
          </Field>
          <Field label={required("Rate (₹)")} helper="Paid per shift or per hour." error={visibleError("rate")}>
            {numberInput("rate")}
          </Field>
          <Field label="Payable Amount (₹)" helper="Calculated: quantity × rate.">
            <TextField value={formatMoney(totals.payable_amount)} disabled onChange={() => {}} />
          </Field>
        </FormSection>

      </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};

const money = (value) => (value == null ? "–" : formatMoney(value));

// showApproval: false when Pay Input Configuration has no approval step, so
// the approval status is left out.
export const ShiftEarningViewPanel = ({ open, row, onClose, onEdit, onDelete, onStatus, canEdit, showApproval = true, expanded, onToggleExpand, domain, busy }) => {
  const [tab, setTab] = useState("info");
  // Each record opens on its Information tab.
  useEffect(() => setTab("info"), [row?.id]);

  if (!row) return null;
  const { indigo, blue, green, orange, purple, pink } = detailAccentPalette;
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Shift Earning"
      subtitle={`Shift earning details · ${formatDate(row.date)}`}
      bodySx={panelBodySx}
      headerActions={
        canEdit ? (
          <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
            <span>
              <Mui.IconButton
                size="small"
                aria-label="Edit"
                disabled={busy}
                onClick={() => onEdit(row)}
                sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </span>
          </Mui.Tooltip>
        ) : null
      }
      // Delete sits in the footer as a full-width button, like the Cancel
      // action on Team Workspace Approvals' detail panels.
      footer={
        canEdit ? (
          <ViewActionFooter
            approval={showApproval ? { status: row.status, disabled: busy, onApprove: onStatus && (() => onStatus(row, "Approved")), onReject: onStatus && (() => onStatus(row, "Rejected")) } : null}
            recordAction={{ label: "Delete Shift Earning", onClick: () => onDelete(row), disabled: busy }}
          />
        ) : null
      }
    >
      {/* Employee card, same as Team Workspace Approvals' view panels. */}
      <Mui.Box sx={{ mb: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: row.image_url,
            employee_id: row.employee_code,
            user_name: row.employee_name,
            department_name: row.department_name || row.department,
            designation: row.designation_name || row.designation,
          }}
          status={showApproval ? row.status : undefined}
        />
      </Mui.Box>
      <Mui.Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="fullWidth"
        sx={{
          ...designShell.panelTabs,
          mt: 0,
          mb: 2,
          borderBottom: `1px solid ${designColors.slate200}`,
        }}
      >
        <Mui.Tab label="Information" value="info" />
        <Mui.Tab label="Activity Log" value="audit" />
      </Mui.Tabs>
      {tab === "audit" ? (
        <PayInputActivityLog domain={domain} type="SHIFT_EARNING" recordId={row.id} refreshKey={row.modifiedat} />
      ) : (
        <Mui.Stack spacing={2}>
          <RequestDetailsSection
            title="SHIFT DETAILS"
            fields={[
              { icon: CalendarMonthOutlined, label: "Date", value: formatDate(row.date), accent: indigo },
              { icon: CategoryOutlined, label: "Day Type", value: row.earning_type || "–", accent: pink },
              { icon: ScheduleOutlined, label: "Shift", value: row.shift_name || "–", accent: blue },
              { icon: BadgeOutlined, label: "Basis", value: row.basis || "–", accent: purple },
            ]}
          />
          <RequestDetailsSection
            title="CALCULATION"
            fields={[
              {
                icon: StorageOutlined,
                label: "Credited − Penalty = Net Qty",
                value: `${formatPayableQty(row.quantity)} − ${formatPayableQty(row.penalty)} = ${formatPayableQty(row.net)}`,
                accent: green,
              },
              {
                icon: StorageOutlined,
                label: "Net Qty × Factor = Payable Qty",
                value: `${formatPayableQty(row.net)} × ${formatExactQty(row.payment_factor)} = ${formatPayableQty(row.adjusted)}`,
                accent: green,
              },
              {
                icon: StorageOutlined,
                label: "Payable Qty × Rate = Earning Amount",
                value: `${formatPayableQty(row.adjusted)} × ${money(row.rate)} = ${money(row.earning_amount)}`,
                accent: orange,
              },
              { icon: StorageOutlined, label: "Gross Pay", value: money(row.gross_pay), accent: orange },
              { icon: StorageOutlined, label: "Net Pay", value: money(row.net_pay), accent: orange },
            ]}
          />
          <RequestDetailsSection
            title="PAYROLL"
            fields={[
              { icon: ApartmentOutlined, label: "Company", value: row.company_name || "–", accent: indigo },
              { icon: EventRepeatOutlined, label: "Payroll Cycle", value: row.payroll_cycle || "–", accent: indigo },
              ...(showApproval ? [{ icon: TaskAltOutlined, label: "Approval Status", value: row.status || "–", accent: blue }] : []),
              { icon: StorageOutlined, label: "Source", value: row.source || "–", accent: purple },
            ]}
          />
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
